import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import { useState, useRef, useEffect, useCallback } from 'react';
import { Button, Tooltip, Popover, Input, Upload, App } from 'antd';
import {
  Bold, Italic, Strikethrough, Code, List, ListOrdered,
  Link2, Image as ImageIcon, Smile, Paperclip, Heading1, Heading2,
  Quote, Undo, Redo, X, FileText,
} from 'lucide-react';
import type { UploadFile } from 'antd';
import EmojiPicker, { EmojiStyle } from 'emoji-picker-react';
import { apiClient } from '@/services/apiClient';

// CSS styles for TipTap editor
const editorStyles = `
.rich-editor-wrapper {
  border: 1px solid #d4d9df;
  border-radius: 6px;
  background: #fff;
  transition: border-color 0.2s;
}
.rich-editor-wrapper:focus-within {
  border-color: #6699CC;
}
.rich-editor-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  padding: 6px 8px;
  border-bottom: 1px solid #eceef1;
  background: #fafbfc;
  border-radius: 6px 6px 0 0;
}
.rich-editor-toolbar .tool-btn {
  min-width: 28px;
  height: 28px;
  padding: 0 6px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #48535e;
}
.rich-editor-toolbar .tool-btn:hover {
  background: #eaeef3;
}
.rich-editor-toolbar .tool-btn.active {
  background: #d5e3f2;
  color: #2a3547;
}
.rich-editor-toolbar .divider {
  width: 1px;
  background: #dfe3e8;
  margin: 2px 4px;
}
.rich-editor-content {
  padding: 10px 12px;
  min-height: 80px;
  max-height: 400px;
  overflow-y: auto;
  font-size: 13px;
  line-height: 1.6;
}
.rich-editor-content .ProseMirror {
  outline: none;
  min-height: 60px;
}
.rich-editor-content .ProseMirror p {
  margin: 0 0 6px 0;
}
.rich-editor-content .ProseMirror p:last-child {
  margin-bottom: 0;
}
.rich-editor-content .ProseMirror a {
  color: #6699CC;
  text-decoration: underline;
  cursor: pointer;
}
.rich-editor-content .ProseMirror img {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
  display: block;
  margin: 6px 0;
}
.rich-editor-content .ProseMirror h1 {
  font-size: 18px;
  font-weight: 600;
  margin: 8px 0;
}
.rich-editor-content .ProseMirror h2 {
  font-size: 16px;
  font-weight: 600;
  margin: 6px 0;
}
.rich-editor-content .ProseMirror ul, .rich-editor-content .ProseMirror ol {
  padding-left: 22px;
  margin: 4px 0;
}
.rich-editor-content .ProseMirror blockquote {
  border-left: 3px solid #d4d9df;
  padding-left: 12px;
  color: #5b6875;
  margin: 6px 0;
}
.rich-editor-content .ProseMirror code {
  background: #f6f7f8;
  padding: 2px 5px;
  border-radius: 3px;
  font-family: 'Menlo', 'Monaco', 'Courier New', monospace;
  font-size: 12px;
}
.rich-editor-content .ProseMirror pre {
  background: #2a3547;
  color: #f6f7f8;
  padding: 10px;
  border-radius: 4px;
  font-family: 'Menlo', 'Monaco', 'Courier New', monospace;
  font-size: 12px;
  overflow-x: auto;
}
.rich-editor-content .ProseMirror p.is-editor-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  color: #adb6c0;
  pointer-events: none;
  height: 0;
}
.rich-editor-attachments {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 8px 10px;
  border-top: 1px solid #eceef1;
  background: #fafbfc;
}
.rich-editor-attachment-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  background: #fff;
  border: 1px solid #e1e5eb;
  border-radius: 4px;
  font-size: 12px;
}
`;

export interface RichAttachment {
  id: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  url: string;
}

export interface RichEditorValue {
  html: string;
  attachments: RichAttachment[];
}

interface RichEditorProps {
  value?: RichEditorValue;
  onChange?: (value: RichEditorValue) => void;
  placeholder?: string;
  minHeight?: number;
  maxHeight?: number;
  showAttachments?: boolean;
  /** Hide toolbar buttons mà không cần thiết cho mô tả (ex: comment) */
  simplified?: boolean;
  /** Callback upload file, return URL đã upload */
  onUploadFile?: (file: File) => Promise<RichAttachment>;
}

const ToolButton = ({
  onClick, active, disabled, title, children,
}: {
  onClick: () => void; active?: boolean; disabled?: boolean; title: string; children: React.ReactNode;
}) => (
  <Tooltip title={title} mouseEnterDelay={0.3}>
    <button
      type="button"
      className={`tool-btn ${active ? 'active' : ''}`}
      onClick={onClick}
      disabled={disabled}
      style={{ opacity: disabled ? 0.4 : 1 }}
    >
      {children}
    </button>
  </Tooltip>
);

/**
 * Default upload handler: POST /api/attachments với multipart
 * Trả về URL dạng `/api/attachments/{path}/download`
 */
async function defaultUploadFile(file: File): Promise<RichAttachment> {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await apiClient.post<any>('/attachments/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return {
    id: data.id,
    fileName: data.fileName ?? file.name,
    fileSize: data.fileSize ?? file.size,
    mimeType: data.mimeType ?? file.type,
    url: data.url ?? `/api/attachments/${data.id}/download`,
  };
}

export function RichEditor({
  value,
  onChange,
  placeholder = 'Nhập nội dung...',
  minHeight = 80,
  maxHeight = 400,
  showAttachments = true,
  simplified = false,
  onUploadFile = defaultUploadFile,
}: RichEditorProps) {
  const { message } = App.useApp();
  const [linkPopoverOpen, setLinkPopoverOpen] = useState(false);
  const [emojiPopoverOpen, setEmojiPopoverOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: simplified ? false : { levels: [1, 2, 3] },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
      }),
      Image.configure({
        inline: false,
        allowBase64: false,
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: value?.html ?? '',
    onUpdate: ({ editor }) => {
      onChange?.({
        html: editor.getHTML(),
        attachments: value?.attachments ?? [],
      });
    },
  });

  // Sync external value change
  useEffect(() => {
    if (!editor || !value) return;
    const currentHtml = editor.getHTML();
    const incoming = value.html ?? '';
    if (currentHtml !== incoming && !editor.isFocused) {
      editor.commands.setContent(incoming, { emitUpdate: false });
    }
  }, [value?.html, editor]);

  // Inject CSS once
  useEffect(() => {
    const id = 'rich-editor-styles';
    if (document.getElementById(id)) return;
    const el = document.createElement('style');
    el.id = id;
    el.innerHTML = editorStyles;
    document.head.appendChild(el);
  }, []);

  const handleInsertLink = useCallback(() => {
    if (!editor) return;
    let url = linkUrl.trim();
    if (!url) {
      editor.chain().focus().unsetLink().run();
      setLinkPopoverOpen(false);
      return;
    }
    if (!/^https?:\/\//i.test(url) && !url.startsWith('/') && !url.startsWith('mailto:')) {
      url = 'https://' + url;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    setLinkUrl('');
    setLinkPopoverOpen(false);
  }, [editor, linkUrl]);

  const openLinkPopover = () => {
    if (!editor) return;
    const currentUrl = editor.getAttributes('link').href;
    setLinkUrl(currentUrl ?? '');
    setLinkPopoverOpen(true);
  };

  const handleInsertEmoji = (emojiData: any) => {
    if (!editor) return;
    editor.chain().focus().insertContent(emojiData.emoji).run();
    setEmojiPopoverOpen(false);
  };

  const handleUploadImage = async (file: File) => {
    try {
      const att = await onUploadFile(file);
      editor?.chain().focus().setImage({ src: att.url, alt: att.fileName }).run();
      message.success('Đã chèn ảnh');
    } catch (e: any) {
      message.error('Lỗi upload ảnh: ' + (e?.response?.data?.message ?? e?.message ?? 'Unknown'));
    }
  };

  const handleUploadFile = async (file: File) => {
    try {
      const att = await onUploadFile(file);
      // Thêm attachment vào value
      const newAtts = [...(value?.attachments ?? []), att];
      onChange?.({
        html: editor?.getHTML() ?? '',
        attachments: newAtts,
      });
      message.success(`Đã đính kèm ${file.name}`);
    } catch (e: any) {
      message.error('Lỗi upload: ' + (e?.response?.data?.message ?? e?.message ?? 'Unknown'));
    }
  };

  const removeAttachment = (id: string) => {
    const newAtts = (value?.attachments ?? []).filter(a => a.id !== id);
    onChange?.({
      html: editor?.getHTML() ?? '',
      attachments: newAtts,
    });
  };

  if (!editor) return null;

  return (
    <div className="rich-editor-wrapper">
      <div className="rich-editor-toolbar">
        <ToolButton title="In đậm (Ctrl+B)" active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}>
          <Bold size={14} />
        </ToolButton>
        <ToolButton title="In nghiêng (Ctrl+I)" active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}>
          <Italic size={14} />
        </ToolButton>
        <ToolButton title="Gạch ngang" active={editor.isActive('strike')}
          onClick={() => editor.chain().focus().toggleStrike().run()}>
          <Strikethrough size={14} />
        </ToolButton>
        <ToolButton title="Code inline" active={editor.isActive('code')}
          onClick={() => editor.chain().focus().toggleCode().run()}>
          <Code size={14} />
        </ToolButton>

        {!simplified && (
          <>
            <div className="divider" />
            <ToolButton title="Tiêu đề 1" active={editor.isActive('heading', { level: 1 })}
              onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>
              <Heading1 size={14} />
            </ToolButton>
            <ToolButton title="Tiêu đề 2" active={editor.isActive('heading', { level: 2 })}
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
              <Heading2 size={14} />
            </ToolButton>
          </>
        )}

        <div className="divider" />
        <ToolButton title="Danh sách" active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}>
          <List size={14} />
        </ToolButton>
        <ToolButton title="Danh sách số" active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          <ListOrdered size={14} />
        </ToolButton>
        <ToolButton title="Trích dẫn" active={editor.isActive('blockquote')}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}>
          <Quote size={14} />
        </ToolButton>

        <div className="divider" />
        <Popover
          open={linkPopoverOpen}
          onOpenChange={setLinkPopoverOpen}
          trigger="click"
          content={
            <div style={{ display: 'flex', gap: 6, width: 320 }}>
              <Input
                placeholder="https://..."
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                onPressEnter={handleInsertLink}
                autoFocus
              />
              <Button type="primary" size="small" onClick={handleInsertLink}>
                Chèn
              </Button>
              {editor.isActive('link') && (
                <Button size="small" danger onClick={() => {
                  editor.chain().focus().unsetLink().run();
                  setLinkPopoverOpen(false);
                }}>
                  Xóa
                </Button>
              )}
            </div>
          }
        >
          <span>
            <ToolButton title="Chèn hyperlink" active={editor.isActive('link')}
              onClick={openLinkPopover}>
              <Link2 size={14} />
            </ToolButton>
          </span>
        </Popover>

        <ToolButton title="Chèn ảnh" onClick={() => imageInputRef.current?.click()}>
          <ImageIcon size={14} />
        </ToolButton>
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleUploadImage(file);
            e.target.value = '';
          }}
        />

        <Popover
          open={emojiPopoverOpen}
          onOpenChange={setEmojiPopoverOpen}
          trigger="click"
          content={
            <EmojiPicker
              onEmojiClick={handleInsertEmoji}
              emojiStyle={EmojiStyle.NATIVE}
              width={320}
              height={360}
              searchPlaceholder="Tìm emoji..."
            />
          }
        >
          <span>
            <ToolButton title="Chèn emoji" onClick={() => setEmojiPopoverOpen(true)}>
              <Smile size={14} />
            </ToolButton>
          </span>
        </Popover>

        {showAttachments && (
          <>
            <ToolButton title="Đính kèm tệp" onClick={() => fileInputRef.current?.click()}>
              <Paperclip size={14} />
            </ToolButton>
            <input
              ref={fileInputRef}
              type="file"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleUploadFile(file);
                e.target.value = '';
              }}
            />
          </>
        )}

        <div className="divider" />
        <ToolButton title="Hoàn tác (Ctrl+Z)"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}>
          <Undo size={14} />
        </ToolButton>
        <ToolButton title="Làm lại (Ctrl+Y)"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}>
          <Redo size={14} />
        </ToolButton>
      </div>

      <div
        className="rich-editor-content"
        style={{ minHeight, maxHeight }}
      >
        <EditorContent editor={editor} />
      </div>

      {showAttachments && (value?.attachments?.length ?? 0) > 0 && (
        <div className="rich-editor-attachments">
          {value!.attachments.map(att => (
            <div key={att.id} className="rich-editor-attachment-item">
              <FileText size={12} style={{ color: '#6699CC' }} />
              <a href={att.url} target="_blank" rel="noopener noreferrer"
                 style={{ color: '#48535e', textDecoration: 'none' }}>
                {att.fileName}
              </a>
              <span style={{ color: '#adb6c0', fontSize: 10 }}>
                ({(att.fileSize / 1024).toFixed(1)} KB)
              </span>
              <X size={12} style={{ cursor: 'pointer', color: '#8a95a5' }}
                 onClick={() => removeAttachment(att.id)} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Render rich content readonly (view mode)
 * Dùng cho hiển thị comment, description đã tạo
 */
export function RichContent({ html, attachments }: { html?: string; attachments?: RichAttachment[] }) {
  // V1.0.65: State cho image lightbox + cache blob URLs cho ảnh
  const [imageBlobUrls, setImageBlobUrls] = useState<Record<string, string>>({});
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  useEffect(() => {
    const id = 'rich-content-styles';
    if (document.getElementById(id)) return;
    const el = document.createElement('style');
    el.id = id;
    el.innerHTML = `
.rich-content { font-size: 13px; line-height: 1.6; color: #2a3547; }
.rich-content p { margin: 0 0 6px 0; }
.rich-content p:last-child { margin-bottom: 0; }
.rich-content a { color: #6699CC; text-decoration: underline; cursor: pointer; }
.rich-content img { max-width: 100%; height: auto; border-radius: 4px; display: block; margin: 6px 0; }
.rich-content h1 { font-size: 18px; font-weight: 600; margin: 8px 0; }
.rich-content h2 { font-size: 16px; font-weight: 600; margin: 6px 0; }
.rich-content h3 { font-size: 14px; font-weight: 600; margin: 4px 0; }
.rich-content ul, .rich-content ol { padding-left: 22px; margin: 4px 0; }
.rich-content blockquote { border-left: 3px solid #d4d9df; padding-left: 12px; color: #5b6875; margin: 6px 0; }
.rich-content code { background: #f6f7f8; padding: 2px 5px; border-radius: 3px; font-family: 'Menlo','Monaco',monospace; font-size: 12px; }
.rich-content pre { background: #2a3547; color: #f6f7f8; padding: 10px; border-radius: 4px; font-family: 'Menlo','Monaco',monospace; font-size: 12px; overflow-x: auto; }
`;
    document.head.appendChild(el);
  }, []);

  // V1.0.65: Tự fetch ảnh qua axios (gửi JWT) → blob URL, vì <img src=/api/attachments/...> qua browser KHÔNG có JWT header
  useEffect(() => {
    if (!attachments) return;
    const imageAtts = attachments.filter(a => isImageMime(a.mimeType, a.fileName));
    imageAtts.forEach(async (a) => {
      if (imageBlobUrls[a.id]) return; // đã có rồi
      try {
        const response = await apiClient.get(`/attachments/${a.id}/download`, { responseType: 'blob' });
        const blob = new Blob([response.data], { type: a.mimeType });
        const objUrl = URL.createObjectURL(blob);
        setImageBlobUrls(prev => ({ ...prev, [a.id]: objUrl }));
      } catch {
        // bỏ qua, fallback dùng link download
      }
    });
    // Cleanup blob URLs khi unmount
    return () => {
      Object.values(imageBlobUrls).forEach(u => {
        try { URL.revokeObjectURL(u); } catch {}
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachments]);

  // V1.0.65: Click vào file (không phải ảnh) → fetch qua axios + trigger download
  const handleDownloadFile = async (att: RichAttachment) => {
    try {
      const response = await apiClient.get(`/attachments/${att.id}/download`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: att.mimeType });
      const objUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objUrl;
      link.download = att.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
    } catch (err) {
      console.error('Download attachment fail:', err);
    }
  };

  // V1.0.65: Click ảnh → mở lightbox
  const handleOpenImage = (att: RichAttachment) => {
    const url = imageBlobUrls[att.id];
    if (url) setLightboxUrl(url);
  };

  if (!html && (!attachments || attachments.length === 0)) return null;

  const imageAtts = (attachments ?? []).filter(a => isImageMime(a.mimeType, a.fileName));
  const fileAtts  = (attachments ?? []).filter(a => !isImageMime(a.mimeType, a.fileName));

  return (
    <div>
      {html && (
        <div
          className="rich-content"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      )}

      {/* V1.0.65: Render ẢNH thành thumbnail grid, click → lightbox */}
      {imageAtts.length > 0 && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8,
        }}>
          {imageAtts.map(att => {
            const blobUrl = imageBlobUrls[att.id];
            return (
              <div
                key={att.id}
                onClick={() => handleOpenImage(att)}
                style={{
                  position: 'relative',
                  width: 120, height: 120,
                  borderRadius: 6,
                  overflow: 'hidden',
                  cursor: blobUrl ? 'zoom-in' : 'default',
                  border: '1px solid #e1e5eb',
                  background: '#f6f7f8',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
                title={att.fileName}
              >
                {blobUrl ? (
                  <img
                    src={blobUrl}
                    alt={att.fileName}
                    style={{
                      width: '100%', height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <span style={{ fontSize: 11, color: '#8a95a5' }}>Đang tải...</span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* V1.0.65: Render FILE thành chip với nút download (qua axios + JWT) */}
      {fileAtts.length > 0 && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8,
          paddingTop: imageAtts.length > 0 ? 8 : 8,
          borderTop: imageAtts.length === 0 ? '1px solid #eceef1' : 'none'
        }}>
          {fileAtts.map(att => (
            <button
              key={att.id}
              type="button"
              onClick={() => handleDownloadFile(att)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '4px 10px', background: '#f6f7f8',
                border: '1px solid #e1e5eb', borderRadius: 4,
                fontSize: 12, color: '#2a3547',
                cursor: 'pointer',
              }}
            >
              <FileText size={12} style={{ color: '#6699CC' }} />
              {att.fileName}
              <span style={{ color: '#8a95a5', fontSize: 10 }}>
                ({(att.fileSize / 1024).toFixed(1)} KB)
              </span>
            </button>
          ))}
        </div>
      )}

      {/* V1.0.65: Lightbox xem ảnh fullscreen */}
      {lightboxUrl && (
        <div
          onClick={() => setLightboxUrl(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.85)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'zoom-out',
            padding: 20,
          }}
        >
          <img
            src={lightboxUrl}
            alt=""
            style={{
              maxWidth: '95vw', maxHeight: '95vh',
              objectFit: 'contain',
              borderRadius: 4,
            }}
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={(e) => { e.stopPropagation(); setLightboxUrl(null); }}
            style={{
              position: 'absolute', top: 16, right: 16,
              width: 36, height: 36, borderRadius: '50%',
              border: 'none', background: 'rgba(255,255,255,0.2)',
              color: '#fff', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            title="Đóng (Esc)"
          >
            <X size={20} />
          </button>
        </div>
      )}
    </div>
  );
}

/** V1.0.65: Helper detect ảnh */
function isImageMime(mime: string | undefined | null, fileName: string | undefined | null): boolean {
  if (mime && mime.startsWith('image/')) return true;
  if (fileName) {
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (ext && ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) return true;
  }
  return false;
}
